import { NavState } from '../nav/nav'
import { ModalSDK } from '../modal/modal.types'
import { PubsubEvent } from '../pubsub/pubsub'
import Messenger from '../messenger/messenger'
import AbandonCartNudgeTrayModel from './abandon-cart-nudge-tray.model'
import AbandonCartNudgeTrayView from './abandon-cart-nudge-tray.view'
import { Configs } from '../config/config.service'
import HostPageEventInstrumentation from '../host-page-events/host-page-event.instrumentation'
import { abandonCartActionEvent } from '../host-page-events/host-page-event.definitions'

interface AbandonCartNudgeTrayCtrlProps {
  win: Window
  msg: Messenger
  modalSDK: ModalSDK
  configs: Promise<Configs>
  hostPageEventInstrumentation?: HostPageEventInstrumentation
}

export default class AbandonCartNudgeTrayCtrl {
  private props: AbandonCartNudgeTrayCtrlProps
  private model: AbandonCartNudgeTrayModel
  private view: AbandonCartNudgeTrayView
  private destroyed = false

  constructor(props: AbandonCartNudgeTrayCtrlProps) {
    this.props = props
    this.init()
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true

    if (this.view) {
      this.view.destroy()
    }
    if (this.model) {
      this.model.destroy()
    }
  }

  private init = async () => {
    const { modalSDK } = this.props

    this.model = new AbandonCartNudgeTrayModel({
      win: this.props.win,
      msg: this.props.msg,
      configs: await this.props.configs,
    })

    this.view = new AbandonCartNudgeTrayView({
      win: this.props.win,
      onCta: this.subscribeToNudgeTrayCtaEvent,
      onClose: this.subscribeToNudgeTrayCloseEvent,
      /**
       * PS. Open the nudge tray on page load only if there's nudge tray state
       * and if the modal is NOT opened.
       */
      isOpen: !!(await this.model.get()) && !modalSDK.isOpen(),
    })

    modalSDK.subscribeToOpenEvent(this.subscribeToModalOpenEvent)
    modalSDK.subscribeToCloseEvent(this.subscribeToModalCloseEvent)

    if (modalSDK.isOpen()) {
      this.model.markModalOpened()
      const campaign = modalSDK.getCampaign()
      if (campaign) {
        this.model.set({ campaign, params: modalSDK.getParams() })
      }
    } else {
      const modelState = await this.model.get()

      if (modelState) {
        this.view.open(modelState)
      }
    }
  }

  private subscribeToModalOpenEvent = (event: PubsubEvent<NavState>) => {
    this.model.markModalOpened()
    this.model.set(event.data)
    this.view.close()
  }

  private subscribeToModalCloseEvent = async () => {
    const modelState = await this.model.get()
    if (modelState) {
      this.view.open(modelState)
    }
  }

  private subscribeToNudgeTrayCloseEvent = async () => {
    const modelState = await this.model.get()

    if (modelState?.nav.campaign) {
      this.props.hostPageEventInstrumentation?.track(abandonCartActionEvent, {
        campaignId: modelState.nav.campaign,
        data: { text: 'Dismiss', tracking_id: 'abandoned cart dismissed' },
      })
    }

    this.model.reset()
    this.view.close()
  }

  private subscribeToNudgeTrayCtaEvent = async () => {
    const modelState = await this.model.get()

    if (modelState?.nav.campaign) {
      this.props.hostPageEventInstrumentation?.track(abandonCartActionEvent, {
        campaignId: modelState.nav.campaign,
        data: { text: 'Give Now', tracking_id: 'abandoned cart give now' },
      })

      this.props.modalSDK.open(modelState.nav.campaign, modelState.nav.params)
    }
  }
}
